import { useCallback } from 'react';

import useLocalStorageState from 'use-local-storage-state';

type CollapseStates = {
  [itemId: string]: boolean;
};

interface UsePersistentCollapseStatesReturn {
  getState: (itemId: string) => boolean;
  setState: (itemId: string, isCollapsed: boolean) => void;
  toggleState: (itemId: string) => void;
  resetState: (itemId: string) => void;
  resetAllStates: () => void;
  getAllStates: () => CollapseStates;
}

const usePersistentCollapseStates = (
  namespace: string,
  defaultCollapsed: boolean = false,
): UsePersistentCollapseStatesReturn => {
  const storageKey = `collapse-states-${namespace}`;

  const [collapseStates, setCollapseStates] =
    useLocalStorageState<CollapseStates>(storageKey, {
      defaultValue: {},
    });

  const getState = useCallback(
    (itemId: string): boolean => {
      return collapseStates[itemId] ?? defaultCollapsed;
    },
    [collapseStates, defaultCollapsed],
  );

  const setState = useCallback(
    (itemId: string, isCollapsed: boolean) => {
      setCollapseStates(prev => ({
        ...prev,
        [itemId]: isCollapsed,
      }));
    },
    [setCollapseStates],
  );

  const toggleState = useCallback(
    (itemId: string) => {
      const currentState = getState(itemId);
      setState(itemId, !currentState);
    },
    [getState, setState],
  );

  const resetState = useCallback(
    (itemId: string) => {
      setCollapseStates(prev => {
        const newStates = { ...prev };
        delete newStates[itemId];
        return newStates;
      });
    },
    [setCollapseStates],
  );

  const resetAllStates = useCallback(() => {
    setCollapseStates({});
  }, [setCollapseStates]);

  const getAllStates = useCallback(() => {
    return collapseStates;
  }, [collapseStates]);

  return {
    getState,
    setState,
    toggleState,
    resetState,
    resetAllStates,
    getAllStates,
  };
};

export default usePersistentCollapseStates;
